import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { LogOut, User, Calendar, Menu, X, AlertTriangle } from "lucide-react";

const Header = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useLanguage();
  const loc = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const isAdmin = loc.pathname.startsWith("/admin");
  const isStation = loc.pathname.startsWith("/station");
  const isCustomer = loc.pathname.startsWith("/my");
  const isCalendar = loc.pathname.startsWith("/calendar");
  const betaNotice = t("beta_notice");
  const isBeta = !!betaNotice && (lang === "ti" || lang === "ar");

  const onLogout = async () => {
    setMobileOpen(false);
    await logout();
    navigate("/login");
  };

  const closeMobile = () => setMobileOpen(false);

  const role = user?.role;
  const roleLabel = role === "admin" ? "Admin" : role === "station_manager" ? "Station" : role === "customer" ? "Kunde" : "";

  return (
    <header className="bg-[var(--rs-primary)] text-white border-b-4 border-black relative z-40" data-testid="header">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 h-20 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img
            src="/nivara-logo-gold.png"
            alt="NIVARA"
            width="160"
            height="44"
            fetchpriority="high"
            decoding="async"
            className="h-9 sm:h-11 w-auto object-contain"
            draggable="false"
          />
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-2">
          <LanguageSwitcher />
          <div className="w-px h-8 bg-white/15 mx-1" />
          {!isCalendar && (
            <Link
              to="/calendar"
              className="text-xs uppercase tracking-[0.2em] px-3 py-3 hover:text-[var(--rs-accent)] transition-colors inline-flex items-center gap-2"
              data-testid="header-calendar-link"
            >
              <Calendar size={14} /> {t("nav_calendar")}
            </Link>
          )}
          {user && (role === "admin" || role === "station_manager") ? (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-2 px-3 py-2 border border-white/15 text-[10px] uppercase tracking-[0.2em] text-white/70">
                <User size={12} />
                <span>{roleLabel}</span>
              </div>
              {role === "admin" && !isAdmin && (
                <Link to="/admin" className="text-xs uppercase tracking-[0.2em] px-4 py-3 border border-white/15 hover:bg-white/10 transition-colors" data-testid="header-admin-link">
                  {t("nav_admin")}
                </Link>
              )}
              {(role === "admin" || role === "station_manager") && (
                <Link to="/admin/anfragen" className="text-xs uppercase tracking-[0.2em] px-4 py-3 border border-white/15 hover:bg-white/10 transition-colors" data-testid="header-requests-link">
                  Anfragen
                </Link>
              )}
              {role === "station_manager" && !isStation && (
                <Link to="/station" className="text-xs uppercase tracking-[0.2em] px-4 py-3 border border-white/15 hover:bg-white/10 transition-colors" data-testid="header-station-link">
                  Station
                </Link>
              )}
              <button type="button" onClick={onLogout} className="text-xs uppercase tracking-[0.2em] px-4 py-3 hover:text-[var(--rs-accent)] transition-colors flex items-center gap-2" data-testid="header-logout-btn">
                <LogOut size={14} />
                <span>{t("nav_logout")}</span>
              </button>
            </div>
          ) : user && role === "customer" ? (
            <div className="flex items-center gap-2">
              {!isCustomer && (
                <Link to="/my" className="text-xs uppercase tracking-[0.2em] px-4 py-3 border border-white/15 hover:bg-white/10 transition-colors" data-testid="header-my-link">
                  {t("nav_my")}
                </Link>
              )}
              <button type="button" onClick={onLogout} className="text-xs uppercase tracking-[0.2em] px-4 py-3 hover:text-[var(--rs-accent)] transition-colors flex items-center gap-2" data-testid="header-logout-btn">
                <LogOut size={14} />
                <span>{t("nav_logout")}</span>
              </button>
            </div>
          ) : (
            <>
              <Link to="/register" className="text-xs uppercase tracking-[0.2em] px-4 py-3 hover:text-[var(--rs-accent)] transition-colors inline-flex" data-testid="header-register-link">
                {t("nav_register")}
              </Link>
              <Link to="/login" className="text-xs uppercase tracking-[0.2em] px-4 py-3 border border-white/15 hover:bg-white/10 transition-colors inline-flex" data-testid="header-login-link">
                {t("nav_login")}
              </Link>
            </>
          )}
        </div>

        {/* Mobile burger */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="md:hidden p-2 text-white hover:bg-white/10 transition-colors"
          aria-label="Menu"
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          className="md:hidden absolute top-full left-0 right-0 bg-[var(--rs-primary)] border-t border-white/10 shadow-2xl"
          data-testid="mobile-menu"
        >
          <div className="flex flex-col">
            <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between">
              <span className="rs-overline !text-white/60">{t("language_label")}</span>
              <LanguageSwitcher />
            </div>

            {!isCalendar && (
              <Link
                to="/calendar"
                onClick={closeMobile}
                className="px-4 py-4 border-b border-white/10 flex items-center gap-3 text-sm uppercase tracking-[0.2em] hover:bg-white/5"
                data-testid="mobile-calendar-link"
              >
                <Calendar size={16} /> {t("nav_calendar")}
              </Link>
            )}

            {user && (role === "admin" || role === "station_manager") ? (
              <>
                <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/70">
                  <User size={14} /> {roleLabel}
                </div>
                {role === "admin" && !isAdmin && (
                  <Link to="/admin" onClick={closeMobile} className="px-4 py-4 border-b border-white/10 text-sm uppercase tracking-[0.2em] hover:bg-white/5" data-testid="mobile-admin-link">
                    {t("nav_admin")}
                  </Link>
                )}
                {role === "station_manager" && !isStation && (
                  <Link to="/station" onClick={closeMobile} className="px-4 py-4 border-b border-white/10 text-sm uppercase tracking-[0.2em] hover:bg-white/5" data-testid="mobile-station-link">
                    Station
                  </Link>
                )}
                <button type="button" onClick={onLogout} className="px-4 py-4 text-left text-sm uppercase tracking-[0.2em] hover:bg-white/5 flex items-center gap-3" data-testid="mobile-logout-btn">
                  <LogOut size={16} /> {t("nav_logout")}
                </button>
              </>
            ) : user && role === "customer" ? (
              <>
                {!isCustomer && (
                  <Link to="/my" onClick={closeMobile} className="px-4 py-4 border-b border-white/10 text-sm uppercase tracking-[0.2em] hover:bg-white/5" data-testid="mobile-my-link">
                    {t("nav_my")}
                  </Link>
                )}
                <button type="button" onClick={onLogout} className="px-4 py-4 text-left text-sm uppercase tracking-[0.2em] hover:bg-white/5 flex items-center gap-3" data-testid="mobile-logout-btn">
                  <LogOut size={16} /> {t("nav_logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/register" onClick={closeMobile} className="px-4 py-4 border-b border-white/10 text-sm uppercase tracking-[0.2em] hover:bg-white/5" data-testid="mobile-register-link">
                  {t("nav_register")}
                </Link>
                <Link to="/login" onClick={closeMobile} className="px-4 py-4 text-sm uppercase tracking-[0.2em] hover:bg-white/5 bg-white/5" data-testid="mobile-login-link">
                  {t("nav_login")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
      {/* Beta translation notice */}
      {isBeta && (
        <div
          className="bg-amber-100 text-amber-900 border-t border-amber-300 text-[11px] sm:text-xs px-4 py-2 flex items-center justify-center gap-2"
          data-testid="beta-translation-notice"
        >
          <AlertTriangle size={14} className="flex-shrink-0" />
          <span className="text-center leading-tight">{betaNotice}</span>
        </div>
      )}
    </header>
  );
};

export default Header;
